import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Calendar, Camera, User, ZoomIn, Download } from "lucide-react"
import { useTimesheetRecords } from "@/hooks/useTimesheetRecords"
import { useProfiles } from "@/hooks/useProfiles"
import { useAuth } from "@/hooks/useAuth"

interface PhotoGalleryProps {
  selectedEmployee?: string
  selectedDate?: string
}

const PhotoGallery = ({ selectedEmployee, selectedDate }: PhotoGalleryProps) => {
  const [filterEmployee, setFilterEmployee] = useState(selectedEmployee || 'all')
  const [filterDate, setFilterDate] = useState(selectedDate || '')
  const [selectedPhoto, setSelectedPhoto] = useState<string>('')
  const { profile } = useAuth()
  const { data: employees } = useProfiles()
  const { data: records } = useTimesheetRecords(filterEmployee === 'all' ? '' : filterEmployee, filterDate)

  // Filter records that have photos
  const recordsWithPhotos = records?.filter(record => record.photo_url) || []

  // Group photos by employee and date
  const groupedPhotos = recordsWithPhotos.reduce((acc, record) => {
    const employee = employees?.find(emp => emp.id === record.employee_id)
    const employeeName = employee?.full_name || 'Funcionário'
    const date = record.record_date
    
    if (!acc[employeeName]) {
      acc[employeeName] = {}
    }
    if (!acc[employeeName][date]) {
      acc[employeeName][date] = []
    }
    
    acc[employeeName][date].push(record)
    return acc
  }, {} as Record<string, Record<string, any[]>>)

  const downloadPhoto = async (photoUrl: string, employeeName: string, recordTime: string) => {
    try {
      const response = await fetch(photoUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${employeeName}_${recordTime}.jpg`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Erro ao baixar foto:', error)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Galeria de Fotos dos Registros
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-2">
              <Label>Colaborador</Label>
              <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os colaboradores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os colaboradores</SelectItem>
                  {employees?.filter(emp => 
                    emp.condominium_id === profile?.condominium_id && 
                    emp.role === 'porteiro'
                  ).map(employee => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
              />
            </div>
          </div>

          {/* Photo Grid */}
          <div className="space-y-8">
            {Object.entries(groupedPhotos).map(([employeeName, dates]) => (
              <div key={employeeName}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {employeeName}
                </h3>
                
                {Object.entries(dates).map(([date, dayRecords]) => (
                  <div key={date} className="mb-6">
                    <h4 className="text-md font-medium mb-3 flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {new Date(date + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </h4>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {dayRecords.map((record, index) => (
                        <div key={index} className="group relative">
                          <div className="aspect-square overflow-hidden rounded-lg border bg-muted">
                            <img
                              src={record.photo_url}
                              alt={`Registro ${record.record_type}`}
                              className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                              onClick={() => setSelectedPhoto(record.photo_url)}
                            />
                          </div>
                          
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setSelectedPhoto(record.photo_url)}
                            >
                              <ZoomIn className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => downloadPhoto(record.photo_url, employeeName, record.record_time)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="mt-2 text-center">
                            <p className="text-xs font-medium">
                              {record.record_type === 'entrada' ? 'Entrada' : 
                               record.record_type === 'saida' ? 'Saída' :
                               record.record_type === 'saida_almoco' ? 'Saída Almoço' : 'Volta Almoço'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(record.record_time).toLocaleTimeString('pt-BR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {recordsWithPhotos.length === 0 && (
            <div className="text-center py-12">
              <Camera className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">Nenhuma foto encontrada</p>
              <p className="text-sm text-muted-foreground">
                As fotos dos registros de ponto aparecerão aqui
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photo Modal */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto('')}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Visualizar Foto</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <img
              src={selectedPhoto}
              alt="Foto do registro"
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default PhotoGallery